import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Send, AlertCircle } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useToast } from '../hooks/useToast';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { trackEvent } from '../utils/posthog';

const Contact: React.FC = () => {
  useScrollToTop();
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Track page view
    trackEvent('page_view', {
      page: 'contact_page'
    });
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      trackEvent('form_validation_error', {
        form: 'contact',
        errors: Object.keys(errors)
      });
      return;
    }
    
    setIsSubmitting(true);
    
    // Track form submission attempt
    trackEvent('contact_form_submit', {
      subject_category: formData.subject
    });
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send message');
      }

      // Track successful submission
      trackEvent('contact_form_success');
      
      showToast('Your message has been sent successfully!', 'success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Track submission error
      trackEvent('contact_form_error', {
        error_message: error.message
      });
      
      showToast(error.message || 'Failed to send message. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">Contact Us</h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Have questions about our services? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-[#F0E9FF] rounded-full flex items-center justify-center mb-4">
                  <MapPin className="h-6 w-6 text-[#7B4AE2]" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Our Location</h3>
                <p className="text-gray-600">
                  123 Education Street<br />
                  Pune, Maharashtra 411001<br />
                  India
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-[#F0E9FF] rounded-full flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-[#7B4AE2]" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Email Us</h3>
                <p className="text-gray-600 mb-2">
                  For general inquiries:
                </p>
                <a href="mailto:info@11thadmissions.com" className="text-[#7B4AE2] hover:underline">
                  info@11thadmissions.com
                </a>
                <p className="text-gray-600 mt-2 mb-2">
                  For support:
                </p>
                <a href="mailto:support@11thadmissions.com" className="text-[#7B4AE2] hover:underline">
                  support@11thadmissions.com
                </a>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-[#F0E9FF] rounded-full flex items-center justify-center mb-4">
                  <Phone className="h-6 w-6 text-[#7B4AE2]" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Call Us</h3>
                <p className="text-gray-600 mb-2">
                  Customer Support:
                </p>
                <a href="tel:+919876543210" className="text-[#7B4AE2] hover:underline">
                  +91 98765 43210
                </a>
                <p className="text-gray-600 mt-2 mb-2">
                  Business Inquiries:
                </p>
                <a href="tel:+919876543211" className="text-[#7B4AE2] hover:underline">
                  +91 98765 43211
                </a>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="p-8">
                  <h2 className="text-2xl font-bold mb-6">Send Us a Message</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`block w-full rounded-md shadow-sm focus:ring-[#7B4AE2] focus:border-[#7B4AE2] sm:text-sm border-gray-300 ${
                          errors.name ? 'border-red-300' : ''
                        }`}
                        required
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`block w-full rounded-md shadow-sm focus:ring-[#7B4AE2] focus:border-[#7B4AE2] sm:text-sm border-gray-300 ${
                          errors.email ? 'border-red-300' : ''
                        }`}
                        required
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number (Optional)
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="block w-full rounded-md shadow-sm focus:ring-[#7B4AE2] focus:border-[#7B4AE2] sm:text-sm border-gray-300"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className={`block w-full rounded-md shadow-sm focus:ring-[#7B4AE2] focus:border-[#7B4AE2] sm:text-sm border-gray-300 ${
                          errors.subject ? 'border-red-300' : ''
                        }`}
                        required
                      >
                        <option value="">Select a subject</option>
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Technical Support">Technical Support</option>
                        <option value="Billing Question">Billing Question</option>
                        <option value="Feature Request">Feature Request</option>
                        <option value="Partnership Opportunity">Partnership Opportunity</option>
                        <option value="Other">Other</option>
                      </select>
                      {errors.subject && (
                        <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Your Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className={`block w-full rounded-md shadow-sm focus:ring-[#7B4AE2] focus:border-[#7B4AE2] sm:text-sm border-gray-300 ${
                          errors.message ? 'border-red-300' : ''
                        }`}
                        required
                      ></textarea>
                      {errors.message && (
                        <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                      )}
                    </div>
                    
                    <div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#7B4AE2] hover:bg-[#6A3ACF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7B4AE2] disabled:bg-[#9D78E9] disabled:cursor-not-allowed"
                        onClick={() => {
                          if (!isSubmitting) {
                            trackEvent('button_click', { button_name: 'send_message', location: 'contact_page' });
                          }
                        }}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </button>
                    </div>
                  </form>
                </div>
                
                <div className="bg-[#F0E9FF] p-8 flex flex-col justify-center">
                  <h3 className="text-xl font-semibold mb-4">Frequently Asked Questions</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">What are your business hours?</h4>
                      <p className="text-gray-600 text-sm">
                        Our support team is available Monday through Friday, 9:00 AM to 6:00 PM IST.
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">How quickly will I receive a response?</h4>
                      <p className="text-gray-600 text-sm">
                        We aim to respond to all inquiries within 24 hours during business days.
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Can I visit your office?</h4>
                      <p className="text-gray-600 text-sm">
                        Yes, you can visit our office during business hours. We recommend scheduling an appointment in advance.
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Do you offer phone support?</h4>
                      <p className="text-gray-600 text-sm">
                        Yes, our customer support team is available via phone during business hours.
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-8">
                    <Link 
                      to="/faq" 
                      className="text-[#7B4AE2] hover:text-[#6A3ACF] font-medium"
                      onClick={() => trackEvent('navigation_click', { link_name: 'view_all_faqs', location: 'contact_page' })}
                    >
                      View all FAQs →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Contact;